import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Pickaxe, Sprout, Building2, Zap, Radio, ArrowRight, ChevronRight, ChevronLeft, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../mockData';

const iconMap = { Pickaxe, Sprout, Building2, Zap, Radio };

const BusinessAreasPage = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Premium Business Sector Images for carousel
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1200&q=90",
      title: t('home.mining'),
      subtitle: "Cobalt, Copper & Gold Resources",
      icon: Pickaxe
    },
    {
      image: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1200&q=90",
      title: t('home.agriculture'),
      subtitle: "Sustainable Farming Solutions",
      icon: Sprout
    },
    {
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=90",
      title: t('home.infrastructure'),
      subtitle: "Modern Development Projects",
      icon: Building2
    },
    {
      image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&q=90",
      title: t('home.renewableenergy'),
      subtitle: "Hydroelectric & Solar Power",
      icon: Zap
    },
    {
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=90",
      title: t('home.telecommunications'),
      subtitle: "Digital Infrastructure Growth",
      icon: Radio
    }
  ];
  
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 8000); // Change slide every 8 seconds
    
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Premium Hero Section with Carousel */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
            />
            {/* Dark overlay for text readability - even lighter for maximum image visibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/75 via-blue-900/60 to-blue-800/50"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content Container */}
        <div className="relative h-full flex items-center pt-20 md:pt-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div 
                className="text-white space-y-4 md:space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-4 md:px-6 py-1.5 md:py-2 text-xs md:text-sm font-semibold">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t('home.investmentSectors')}
                </Badge>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  {t('business.title')}
                </h1>
                
                <p className="text-lg sm:text-xl md:text-2xl text-blue-100 leading-relaxed font-light">
                  {t('business.subtitle')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-2 md:pt-4">
                  <Button 
                    onClick={() => document.getElementById('sectors-grid').scrollIntoView({ behavior: 'smooth' })}
                    size="lg" 
                    className="bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-10 py-5 md:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 group"
                  >
                    {t('home.exploreOpportunities')}
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Link to="/contact">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm text-base md:text-lg px-6 md:px-10 py-5 md:py-7 font-semibold w-full"
                    >
                      {t('common.getInTouch')}
                    </Button>
                  </Link>
                </div>
                
                {/* Slide Indicators */}
                <div className="flex gap-3 pt-2 md:pt-4">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'w-12 bg-white' 
                          : 'w-8 bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </motion.div>
              
              {/* Right Side - Sector Info Card */}
              <motion.div 
                className="relative hidden lg:block"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl"
                  >
                    <div className="space-y-6">
                      {/* Sector Icon */}
                      <div className="bg-white/20 backdrop-blur-sm p-4 rounded-2xl w-fit">
                        {React.createElement(heroSlides[currentSlide].icon, {
                          className: "w-12 h-12 text-white"
                        })}
                      </div>
                      
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-blue-200 uppercase tracking-wider">
                          Investment Sector
                        </div>
                        <h3 className="text-3xl font-bold text-white">
                          {heroSlides[currentSlide].title}
                        </h3>
                        <p className="text-xl text-blue-100">
                          {heroSlides[currentSlide].subtitle}
                        </p>
                      </div>
                      
                      <div className="pt-2">
                        <Link to="/contact">
                          <Button 
                            variant="ghost" 
                            className="text-white hover:bg-white/20 group"
                          >
                            Learn More 
                            <ChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20 z-10"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20 z-10"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </section>

      {/* Business Areas Grid */}
      <section id="sectors-grid" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {mockData.businessAreas.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <Card key={area.id} className="overflow-hidden border-2 hover:border-blue-500 hover:shadow-2xl transition-all duration-500 group">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={area.image}
                      alt={t(`home.${area.title.toLowerCase().replace(/\s+/g, '')}`)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/50 to-transparent"></div>
                    <div className="absolute bottom-6 left-6">
                      <div className="bg-white p-5 rounded-2xl shadow-xl group-hover:scale-110 transition-transform">
                        <IconComponent className="w-10 h-10 text-blue-900" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-3xl text-blue-900 group-hover:text-blue-700 transition-colors">
                      {t(`home.${area.title.toLowerCase().replace(/\s+/g, '')}`)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-gray-700 leading-relaxed text-lg">
                      {t(`home.${area.title.toLowerCase().replace(/\s+/g, '')}Desc`)}
                    </CardDescription>
                    <Link to="/contact">
                      <Button className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white group">
                        {t('business.inquireNow')} <ChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Premium CTA with Carousel */}
          <div className="relative overflow-hidden rounded-3xl mt-16">
            {/* Background Carousel */}
            <AnimatePresence mode="sync">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-blue-900/75 to-blue-800/65"></div>
              </motion.div>
            </AnimatePresence>
            
            {/* Content */}
            <div className="relative text-center text-white p-16 md:p-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-4xl md:text-5xl font-bold">{t('business.readyToInvest')}</h2>
                <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto font-light">
                  {t('business.letsDiscuss')}
                </p>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-white text-blue-900 hover:bg-blue-50 px-12 py-7 text-lg shadow-2xl hover:shadow-white/30 transition-all duration-300 group"
                  >
                    {t('business.contactToday')} 
                    <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" />
                  </Button>
                </Link>
                
                {/* Slide Indicators */}
                <div className="flex gap-2 justify-center pt-6">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1 rounded-full transition-all duration-300 ${
                        index === currentSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessAreasPage;
